/**
 * Helfer für die Verknüpfung von App-Terminen (Supabase `termine`) mit
 * gespiegelten Google-Calendar-Events. Bewusst REINE Funktionen — voll
 * unit-testbar (siehe calendarDedup.test.ts), ohne React/Netzwerk.
 *
 * Hintergrund: Der Kalender (KalenderPage) mischt zwei Quellen — die
 * `termine`-Tabelle und Google Calendar. Sobald ein Termin in BEIDE Systeme
 * geschrieben wird, muss er über `google_event_id` verknüpft werden, damit er
 * nicht doppelt im Kalender erscheint.
 */

export interface LinkableTermin {
  google_event_id?: string | null
}

export interface IdentifiableGoogleEvent {
  id: string
}

/**
 * Entfernt Google-Events, die bereits durch eine `termine`-Zeile gespiegelt
 * werden (verknüpft via `google_event_id`). Verhindert die Doppelanzeige
 * desselben Termins, sobald das Mirroring (App → Google) aktiv ist.
 *
 * Heute (noch keine verknüpften Termine) ist das Ergebnis identisch mit der
 * Eingabe — die Funktion ist also rückwärtskompatibel und regressionsfrei.
 */
export function dedupeGoogleEvents<G extends IdentifiableGoogleEvent>(
  googleEvents: G[] | null | undefined,
  termine: LinkableTermin[] | null | undefined,
): G[] {
  const events = googleEvents ?? []
  const linkedIds = new Set(
    (termine ?? [])
      .map(t => t.google_event_id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0),
  )
  if (linkedIds.size === 0) return events
  return events.filter(e => !linkedIds.has(e.id))
}

export interface NewCalendarEventInput {
  summary: string
  description?: string
  location?: string
  start: string // ISO 8601
  end: string // ISO 8601
}

export interface TerminLinkPayload {
  titel: string
  beschreibung: string | null
  ort: string | null
  beginn: string
  ende: string
  business_unit: string
  ganztaegig: boolean
  google_event_id: string
  google_calendar_id: string
  google_synced_at: string
}

/**
 * Baut die `termine`-INSERT-Payload für einen frisch in Google Calendar
 * erstellten Termin, sodass App und Google über `google_event_id` verknüpft
 * sind. Leere/whitespace-Felder werden zu NULL normalisiert.
 */
export function buildTerminLinkPayload(
  event: NewCalendarEventInput,
  googleEventId: string,
  businessUnit: string,
  calendarId = 'primary',
  now: Date = new Date(),
): TerminLinkPayload {
  const trimmedDesc = event.description?.trim()
  const trimmedLoc = event.location?.trim()
  return {
    titel: event.summary,
    beschreibung: trimmedDesc ? trimmedDesc : null,
    ort: trimmedLoc ? trimmedLoc : null,
    beginn: new Date(event.start).toISOString(),
    ende: new Date(event.end).toISOString(),
    business_unit: businessUnit,
    ganztaegig: false,
    google_event_id: googleEventId,
    google_calendar_id: calendarId,
    google_synced_at: now.toISOString(),
  }
}
